import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiEye, FiShield } from 'react-icons/fi';

const PlatformOrganisations = () => {
  const { success, error: showError } = useToast();
  const [organisations, setOrganisations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const loadAll = () => {
    setLoading(true);
    apiClient.get('/platform/organisations', {
      params: { page: 1, limit: 100, search: search || undefined, status: statusFilter || undefined },
    })
      .then(({ data }) => {
        setOrganisations(data.data.items);
        setTotal(data.data.total);
      })
      .catch((err) => showError(getErrorMessage(err, 'Could not load organisations')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, [search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewOrg = async (org) => {
    setSelected(org);
    setMembers(null);
    setSubscription(null);
    try {
      const [membersRes, subRes] = await Promise.all([
        apiClient.get(`/platform/organisations/${org.id}/members`),
        apiClient.get(`/platform/organisations/${org.id}/subscription`),
      ]);
      setMembers(membersRes.data.data);
      setSubscription(subRes.data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load organisation detail')); }
  };

  const handleUpdateStatus = async (orgId, status) => {
    if (status === 'suspended' && !window.confirm('Suspend this organisation? Its users will lose access until reactivated.')) return;
    try {
      await apiClient.patch(`/platform/organisations/${orgId}/status`, { status });
      success(`✅ Organisation ${status}`);
      loadAll();
      if (selected?.id === orgId) setSelected((prev) => ({ ...prev, status }));
    } catch (err) { showError(getErrorMessage(err, 'Could not update organisation status')); }
  };

  const handleToggleOverride = async (orgId, currentlyEnabled) => {
    const confirmMsg = currentlyEnabled
      ? 'Remove full-access override? This organisation will go back to being restricted by its actual subscription plan.'
      : 'Grant full-access override? This organisation will get every feature regardless of what plan they\'re on.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const { data } = await apiClient.patch(`/platform/organisations/${orgId}/override`, { enabled: !currentlyEnabled });
      success(currentlyEnabled ? '✅ Override removed' : '✅ Full access granted');
      setSelected(data.data);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update override')); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Organisations</h1>
        <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>Every tenant on the platform — {total} total</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          placeholder="Search by legal name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, padding: '0.5rem' }}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ padding: '0.5rem' }}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="disabled">Disabled</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      <div className="table-container" style={{ background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
        <table className="data-table">
          <thead><tr><th>Legal Name</th><th>Trading Name</th><th>Status</th><th>Override</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
            ) : organisations.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No organisations match</td></tr>
            ) : organisations.map((org) => (
              <tr key={org.id}>
                <td style={{ fontWeight: 600 }}>{org.legalName}</td>
                <td>{org.tradingName || '—'}</td>
                <td>
                  <span className="badge" style={{
                    background: org.status === 'active' ? '#10b981' : org.status === 'suspended' ? '#ef4444' : '#9ca3af',
                    color: 'white',
                  }}>{org.status}</span>
                </td>
                <td>
                  {org.platformOverrideFullAccess ? (
                    <span className="badge" style={{ background: '#7c3aed', color: 'white' }}>Full Access</span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>—</span>
                  )}
                </td>
                <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="actions">
                    <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewOrg(org)}>
                      <FiEye size={14} /> View
                    </button>
                    {org.status === 'suspended' ? (
                      <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleUpdateStatus(org.id, 'active')}>Reactivate</button>
                    ) : (
                      <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleUpdateStatus(org.id, 'suspended')}>Suspend</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div style={{ marginTop: '1.5rem', background: 'white', borderRadius: '8px', padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{selected.legalName}</h2>
            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelected(null)}>Close</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            <div><div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Company Reg. No.</div><div>{selected.companyRegistrationNumber || '—'}</div></div>
            <div><div style={{ fontSize: '0.8rem', color: '#6b7280' }}>VAT Number</div><div>{selected.vatNumber || '—'}</div></div>
            <div><div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Business Type</div><div>{selected.businessType || '—'}</div></div>
          </div>

          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: selected.platformOverrideFullAccess ? '#f5f3ff' : '#f9fafb', border: `1px solid ${selected.platformOverrideFullAccess ? '#ddd6fe' : '#e5e7eb'}`, borderRadius: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <FiShield size={16} /> Full Access Override
                </div>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                  {selected.platformOverrideFullAccess
                    ? 'This organisation has every feature regardless of its subscription plan.'
                    : 'This organisation is restricted to whatever its current plan includes.'}
                </p>
              </div>
              <button
                className="btn btn-sm btn-primary"
                style={{ width: 'auto' }}
                onClick={() => handleToggleOverride(selected.id, selected.platformOverrideFullAccess)}
              >
                {selected.platformOverrideFullAccess ? 'Remove Override' : 'Grant Full Access'}
              </button>
            </div>
          </div>

          <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Subscription</h3>
          {subscription === null ? (
            <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1.5rem' }}>Loading...</p>
          ) : subscription ? (
            <p style={{ marginBottom: '1.5rem' }}>Status: <strong>{subscription.status}</strong></p>
          ) : (
            <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1.5rem' }}>No subscription on record.</p>
          )}

          <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Members</h3>
          {members === null ? (
            <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Loading...</p>
          ) : members.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>No members.</p>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>User ID</th><th>Status</th><th>Primary Contact</th></tr></thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td>{m.userId}</td>
                      <td>{m.status}</td>
                      <td>{m.isPrimaryContact ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default PlatformOrganisations;